// src/components/ExportConfigModal.jsx
import { useEffect, useRef, useState } from "react";
import { X, Download } from "lucide-react";
import { redirectToLogin } from "../utils/auth";

export default function ExportConfigModal({
  open,
  onClose,
  watchlistOptions = [],
  initialRange = "30d",
  jwtToken,
  // where we fetch csv_export_limit from:
  licenseUrl = "https://whiteintel.io/api/regular/app/v2/account/subscription.php",
}) {
  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  const [entry, setEntry] = useState("ALL");
  const [range, setRange] = useState(initialRange);
  const [markResolved, setMarkResolved] = useState(false);

  const [loading, setLoading] = useState(false);       // export progress
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // csv export allowance
  const [csvLimit, setCsvLimit] = useState(null);      // null=unknown, number >=0
  const [limitLoading, setLimitLoading] = useState(false);
  const [limitError, setLimitError] = useState("");

  const dialogRef = useRef(null);

  // focus trap-ish
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    dialogRef.current?.focus();
    return () => prev && prev.focus && prev.focus();
  }, [open]);

  // fetch csv_export_limit on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      setLimitLoading(true);
      setLimitError("");
      try {
        const res = await fetch(licenseUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken || getJwtToken()}`,
          },
          body: JSON.stringify({}), // keep flexible
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const limit = extractCsvExportLimit(json);
        if (!cancelled) setCsvLimit(limit);
      } catch (e) {
        if (!cancelled) {
          setLimitError(e.message || "Failed to load export allowance");
          setCsvLimit(null);
        }
      } finally {
        if (!cancelled) setLimitLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, jwtToken, licenseUrl]);

  if (!open) return null;

  // helper: compute dateFrom/dateTo based on selected range
  function computeDateRange(r) {
    const now = new Date();
    const dateTo = now.toISOString().split("T")[0];
    let dateFrom = null;

    if (r === "7d") {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      dateFrom = d.toISOString().split("T")[0];
    } else if (r === "30d") {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      dateFrom = d.toISOString().split("T")[0];
    } else if (r === "90d") {
      const d = new Date();
      d.setDate(d.getDate() - 90);
      dateFrom = d.toISOString().split("T")[0];
    }
    // "all" -> both null
    return { dateFrom, dateTo: r === "all" ? null : dateTo };
  }

  async function handleExport() {
    setError(null);
    setSuccess(null);

    // block when allowance is explicitly 0
    if (csvLimit === 0) {
      setError("You have no CSV exports remaining on your current plan.");
      return;
    }

    setLoading(true);
    const { dateFrom, dateTo } = computeDateRange(range);
    const body = {
      entry: entry === "ALL" ? null : entry,
      dateFrom,
      dateTo,
      markResolved,
    };

    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/watchlist/event_export.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken || getJwtToken()}`,
          },
          body: JSON.stringify(body),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      
if (!res.ok) {
  let msg = `Export failed: ${res.status}`;

  try {
    const ct = res.headers.get("content-type") || "";

    if (ct.includes("application/json")) {
      const body = await res.json();
      const detail =
        body?.error ??
        body?.message ??
        // fallback: first string value in the object
        Object.values(body).find((v) => typeof v === "string");
      if (detail) msg = `Export failed: ${detail}`;
    } else {
      const text = await res.text();
      // try to parse JSON-looking text like {"error":"Missing entry"}
      if (text?.trim()?.startsWith("{")) {
        try {
          const body = JSON.parse(text);
          const detail =
            body?.error ??
            body?.message ??
            Object.values(body).find((v) => typeof v === "string");
          if (detail) msg = `Export failed: ${detail}`;
          else msg = `Export failed: ${text}`;
        } catch {
          msg = `Export failed: ${text}`;
        }
      } else {
        msg = `Export failed: ${text}`;
      }
    }
  } catch {
    // keep default msg if parsing fails
  }

  throw new Error(msg);
}


      // download
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "event_export.csv";
      a.click();
      window.URL.revokeObjectURL(url);

      setSuccess("Export completed successfully!");

      // optimistic decrement (if we know the limit and it's >0)
      if (typeof csvLimit === "number" && csvLimit > 0) {
        setCsvLimit(csvLimit - 1);
      }
    } catch (err) {
      console.error("Export error:", err);
      setError(err.message || "Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const exportDisabled = loading || csvLimit === 0;

  return (
    <div className="fixed inset-0 z-[120]" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Panel */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[#000414] border-l border-neutral-800 shadow-[0_0_20px_rgba(56,189,248,0.08)] outline-none flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800">
          <div className="min-w-0">
            <div className="text-sm text-neutral-400">Export Events</div>
            <div className="flex items-center gap-3">
              <div className="text-xl font-semibold truncate">
                Event Export Navigator
              </div>
              {/* CSV limit chip */}
              <div className="shrink-0">
                {limitLoading ? (
                  <span className="text-xs text-neutral-400">Checking allowance…</span>
                ) : limitError ? (
                  <span className="text-xs text-amber-300">Allowance check failed</span>
                ) : csvLimit === 0 ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
                    0 exports left
                  </span>
                ) : typeof csvLimit === "number" ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                    {csvLimit} export{csvLimit === 1 ? "" : "s"} left
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-white/5"
            title="Close"
          >
            <X className="h-5 w-5 text-neutral-300" />
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="sticky top-0 z-40 px-6 pt-3 bg-transparent">
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 backdrop-blur-sm px-3 py-2 text-sm text-red-200 flex items-start justify-between">
              <span className="pr-4">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-xs text-neutral-400 hover:text-white"
                aria-label="Dismiss error"
                type="button"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Success banner */}
        {success && (
          <div className="sticky top-0 z-40 px-6 pt-3 bg-transparent">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-sm px-3 py-2 text-sm text-emerald-200 flex items-start justify-between">
              <span className="pr-4">{success}</span>
              <button
                onClick={() => setSuccess(null)}
                className="ml-3 text-xs text-neutral-400 hover:text-white"
                aria-label="Dismiss success"
                type="button"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Hard block notice when 0 left */}
        {csvLimit === 0 && (
          <div className="mx-5 mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            You have no CSV exports remaining on your current plan. Upgrade or wait
            until your quota resets to enable exporting.
          </div>
        )}

        {/* Body */}
        <div className="p-5 space-y-6 overflow-y-auto flex-1">
          <div className="rounded-lg border border-sky-900/30 bg-white/5 p-4 text-sm text-neutral-200">
            Export a CSV of events matching your filters. You can scope by
            watchlist entry and time interval. Optionally mark all exported
            events as <span className="text-emerald-300 font-medium">resolved</span> after export.
          </div>

          {/* Watchlist entry */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">
              Watchlist Entry
            </div>
            <select
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              className="w-full h-11 rounded-lg border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
            >
              <option value="ALL">Watchlist Entries</option>
              {watchlistOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </section>

          {/* Time range */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">
              Time Interval
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {["7d", "30d", "90d", "all"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setRange(opt)}
                  className={`h-10 rounded-lg border text-sm ${
                    range === opt
                      ? "border-sky-500/40 bg-sky-500/10 text-white"
                      : "border-sky-900/30 bg-transparent text-neutral-300 hover:bg-neutral-900/40"
                  }`}
                >
                  {opt.toUpperCase()}
                </button>
              ))}
            </div>
          </section>

          {/* Mark resolved */}
          <section className="flex items-center gap-3">
            <input
              id="markResolved"
              type="checkbox"
              checked={markResolved}
              onChange={(e) => setMarkResolved(e.target.checked)}
              className="h-4 w-4 accent-sky-500"
            />
            <label htmlFor="markResolved" className="text-sm text-neutral-200">
              Mark all exported events as{" "}
              <span className="text-emerald-300 font-medium">resolved</span>
            </label>
          </section>
        </div>

        {/* Actions */}
        <div className="p-5 flex items-center justify-end gap-3 border-t border-neutral-800">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-lg border border-neutral-700 text-neutral-200 hover:bg-white/5"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exportDisabled}
            title={
              csvLimit === 0
                ? "No CSV exports remaining on your plan"
                : undefined
            }
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg border
              ${exportDisabled
                ? "opacity-60 cursor-not-allowed border-sky-600/20 bg-sky-500/5 text-sky-300"
                : "border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
              }`}
          >
            <Download className="h-4 w-4" />
            {loading ? "Exporting..." : "Export"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */

// Try to read csv_export_limit from various backend shapes.
function extractCsvExportLimit(json) {
  try {
    if (!json || typeof json !== "object") return null;

    // flat: { csv_export_limit: 3 }
    if ("csv_export_limit" in json) {
      return toNonNegInt(json.csv_export_limit);
    }
    // nested in data: { data: { csv_export_limit: 3 } }
    if (json.data && typeof json.data === "object" && "csv_export_limit" in json.data) {
      return toNonNegInt(json.data.csv_export_limit);
    }
    // nested in subscription: { subscription: { csv_export_limit: 3 } }
    if (json.subscription && typeof json.subscription === "object" && "csv_export_limit" in json.subscription) {
      return toNonNegInt(json.subscription.csv_export_limit);
    }
  } catch (_) {}
  return null;
}

function toNonNegInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.trunc(n));
}
